import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Container,
  Card,
  CardBody
} from 'reactstrap';

import { MoreVertical, PlusCircle } from 'react-feather';
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseURL } from '../../../Services/api/baseURL';
import Loader from '../../Loader/Loader';
import { FaPen, FaTrashAlt } from 'react-icons/fa';

const ProductsTable = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10, totalRows: 0 });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [keyValue, setKeyValue] = useState('');
  const [updatedValue, setUpdatedValue] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch configs
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/api/config`);
      const list = response?.data?.data || [];
      setProducts(list);
      setPagination((prev) => ({ ...prev, totalRows: list.length }));
    } catch (error) {
      setProducts([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch configs'
      });
    }
    setLoading(false);
  };

  // Open modal for new config
  const handleAdd = () => {
    setSelectedProduct(null);
    setKeyValue('');
    setUpdatedValue('');
    setModalOpen(true);
  };

  // Open modal for update
  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setKeyValue(product?.key || '');
    setUpdatedValue(product?.value || '');
    setModalOpen(true);
  };

      const deleteSettings = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            const token = await JSON.parse(localStorage.getItem("token"))
            try {
                const data = await axios.delete(`${baseURL}/api/config/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                })
                fetchProducts();
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: data?.data?.message || 'Configuration has been deleted.'
                })
            } catch (error) {
                console.log(error, 'delete error')
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to delete configuration'
                })
            }
        }
    }

  // Save (create or update) config
  const handleSave = async () => {
    try {
      await axios.post(`${baseURL}/api/config`, {
        _id: selectedProduct?._id || undefined, // only pass when updating
        key: selectedProduct?._id ? selectedProduct.key : keyValue, // key is fixed when updating
        value: Number(updatedValue)
      });

      Swal.fire({
        icon: 'success',
        title: selectedProduct?._id ? 'Updated' : 'Created',
        text: `Configuration ${selectedProduct?._id ? 'updated' : 'created'} successfully!`
      });

      setModalOpen(false);
      fetchProducts();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.response?.data?.message || 'Could not save config'
      });
    }
  };

  const columns = [
    // {
    //   name: "Id",
    //   selector: row => row?._id,
    //   sortable: true,
    // },
    {
      name: 'Configuration',
      selector: row => row?.key,
      sortable: true,
      cell: (row) => (<div style={{ fontWeight: "bold" }}>{row?.key}</div>)
    },
    {
      name: 'Value',
      selector: row => row?.value || 'N/A',
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-end align-items-center" style={{ marginRight: "20px" }}>
          <UncontrolledDropdown className="action_dropdown">
            <DropdownToggle className="action_btn">
              <MoreVertical color="#000" size={16} />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => handleOpenModal(row)}>
                Update <FaPen style={{ marginLeft: "5px" }} />
              </DropdownItem>
                 <DropdownItem className='delete_item' onClick={(rowData) => {
                                deleteSettings(row?._id);
                            }}>
                                Delete
                                <FaTrashAlt />
                            </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      ),
      right: true,
    },
  ];

  const paginatedData = products.slice(
    (pagination.page - 1) * pagination.perPage,
    pagination.page * pagination.perPage
  );

  const handlePageChange = (page) => setPagination((prev) => ({ ...prev, page }));
  const handleRowsPerPageChange = (perPage) => setPagination({ page: 1, perPage, totalRows: products.length });

  return (
    <Container fluid={true} style={{ paddingTop: '30px' }}>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <div>
                {/* Header */}
                <Row className="mb-3">
                  <Col className="d-flex justify-content-between align-items-center">
                    <h4>Settings</h4>
                    <Button
                      className='btn btn-primary d-flex align-items-center'
                      onClick={handleAdd}
                    >
                      <PlusCircle size={16} className="me-1" /> Add Config
                    </Button>
                  </Col>
                </Row>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={paginatedData}
                  progressPending={loading}
                  progressComponent={<Loader />}
                  pagination
                  paginationServer={false}
                  paginationTotalRows={pagination.totalRows}
                  paginationPerPage={pagination.perPage}
                  paginationRowsPerPageOptions={[10, 20, 50, 100]}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handleRowsPerPageChange}
                />

                {/* Add/Update Modal */}
                <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
                  <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    {selectedProduct?._id ? 'Update Configuration' : 'Add Configuration'}
                  </ModalHeader>
                  <ModalBody>
                    <Form>
                      <FormGroup>
                        <Label>Key</Label>
                        <Input
                          type="text"
                          value={keyValue}
                          onChange={(e) => setKeyValue(e.target.value)}
                          disabled={!!selectedProduct?._id} // disable key field in update mode
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Value</Label>
                        <Input
                          type="text"
                          value={updatedValue}
                          onChange={(e) => setUpdatedValue(e.target.value)}
                        />
                      </FormGroup>
                    </Form>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button color="primary" onClick={handleSave}>
                      {selectedProduct?._id ? 'Update' : 'Create'}
                    </Button>
                  </ModalFooter>
                </Modal>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>

  );
};

export default ProductsTable;